/* eslint-disable no-restricted-imports */
import React, {ReactNode, useMemo} from 'react'
import styles from './UIGamePlayerOptionsDropdown.module.scss'
import {TradeEmbargoControllerReader, UserReportReasonEnum} from '@colonist/game-base-types'
import {LobbyImage} from '@colonist/types'
import {PlayerValidator} from '@colonist/game-mechanic-validators'
import {navigateToPage} from '../../../../../code/client/shared/scripts/ui_utils/ClientUtils'
import {ViewPopup} from '../../../../../code/client/shared/scripts/ui_popup/ViewPopup'
import {CA} from '../../../../../code/client/shared/scripts/analytics/Analytics'
import {ClientUserState} from '../../../../../code/client/shared/scripts/ClientUserState'
import {AjaxDataTypes} from '../../../../../code/shared/AjaxDataTypes'
import {AnalyticEventCategory, AnalyticEventType, GiftMembershipMethod} from '../../../../../code/shared/entity_datas/EntityDatas'
import {getLobbyImagePath} from '../../../../../code/client/shared/scripts/ui_utils/Utils'
import {Dropdown} from '../Dropdown'
import {UIPopupReportPlayer} from '../../../../../code/client/shared/scripts/ui_popup/UIPopupReportPlayer'
import {UIAjaxWrapper} from '../../../../../code/client/shared/scripts/network/UIAjaxWrapper'
import {socket, uiManagerEvents} from '../../../../../code/client/shared/scripts/EntryShared'
import {UIAPIEndpoints} from '../../../../../code/client/shared/scripts/network/UIAPIEndpoints'
import {T} from '../T'
import {UIUtilsFriends} from '../../../../../code/client/shared/scripts/ui_friends/UIUtilsFriends'
import {UIUtilsEjsVariables} from '../../../../../code/client/shared/scripts/UIUtilsEjsVariables'
import {doActionIfCanGiftMembership} from '../../../../../code/client/shared/scripts/store/StoreUtils'
import {UIUrlParameterNames} from '../../../../../code/client/shared/scripts/ui_html/UIUrlController'
import {useUIGameSocketContext} from '../../contexts/UIGameSocketContext'
import {useGameStore} from '../../store/GameStoreContext'
import {useIsReplay} from '../../store/selectors/useIsReplay'
import {UIViewProfilePopup} from '../../../../../code/client/shared/scripts/profile/UIViewProfilePopup'

type DropdownOptions = 'sendGift' | 'addFriend' | 'viewProfile' | 'blockTrades' | 'mute' | 'report'

interface UIGamePlayerOptionsDropdownProps {

    className?: string
    playerValidator: PlayerValidator
    children: ReactNode
    excludeOptions?: DropdownOptions[]
    isInternalDiscordGift?: boolean
}

export const UIGamePlayerOptionsDropdown = ({
    playerValidator,
    children,
    className,
    excludeOptions = [],
    isInternalDiscordGift = false,
}: UIGamePlayerOptionsDropdownProps) => {
    const myColor = useGameStore((state) => state.myColor)
    const isReplay = useIsReplay()
    const tradeState = useGameStore((state) => state.gameState.tradeState)
    const mutedPlayers = useGameStore((state) => state.gameMute.mutedPlayers)
    const toggleMuteForPlayer = useGameStore((state) => state.gameMute.toggleMuteForPlayer)
    const {sendReportPlayer} = useUIGameSocketContext()
    const onClickToggleEmbargoAgainstPlayer = useGameStore((state) => state.gameTrade.onClickToggleEmbargoAgainstPlayer)
    const isMyColorSpectator = useGameStore((state) => state.isMyColorSpectator)
    const isPlayerMuted = mutedPlayers.includes(playerValidator.username)

    const isTradeBlocked = useMemo(() => TradeEmbargoControllerReader
        .doesPlayer1HaveAnEmbargoAgainstPlayer2(tradeState.embargoState, myColor, playerValidator.color)
    , [tradeState, myColor, playerValidator.color])
    const isCurrentUser = playerValidator.userState.userId === ClientUserState.userState?.id
    const isPlayerMyPerspective = playerValidator.color === myColor

    const sendGiftOption = {
        label: <div className={styles.sendGift}>
            <img src={getLobbyImagePath(LobbyImage.StoreGiftMembershipDark)} /> <T id={{key: 'strings:storePage.sendGift'}} />
        </div>,
        onClick: () => {
            const giftButtonClickEventData: AjaxDataTypes.AnalyticData = {
                type: AnalyticEventType.ABTestClickAnalytics,
                category: AnalyticEventCategory.GiftMembershipOverlay,
                floatValue: GiftMembershipMethod.InGame,
            }
            void CA.sendAnalyticEvent(giftButtonClickEventData)
            if(UIUtilsEjsVariables.isUserOnDiscord && isInternalDiscordGift) {
                doActionIfCanGiftMembership(() => {
                    uiManagerEvents.handleUrlRedirectViaParameters(UIUrlParameterNames.DiscordGifting, playerValidator.username)
                }, playerValidator.username)
            } else {
                const url = (UIUtilsEjsVariables.isUserOnDiscord ? 'https://colonist.io' : '') + `/store?username=${playerValidator.username}#gift`
                doActionIfCanGiftMembership(() => navigateToPage(url, true), playerValidator.username)
            }
        },
        hidden: excludeOptions.includes('sendGift')
        || UIUtilsEjsVariables.isUserOnDiscordMobile
        || UIUtilsEjsVariables.isUserOnMobileAppOrWeb && !isInternalDiscordGift
        || !playerValidator.isUser
        || isCurrentUser
        || playerValidator.isLocalDiscordPlayer,
    }

    const addFriendOption = {
        label: <T id={{key: 'strings:game.playerOptionsPopup.addFriend'}} />,
        onClick: () => {
            // TODO: Migrate All Dialog to React
            new ViewPopup({key: 'strings:popups.addFriend.title'}, {
                key: 'strings:popups.addFriend.body', options: {
                    username: playerValidator.username,
                },
            }, () => void UIUtilsFriends.addFriendAction(playerValidator.username), () => undefined, true).show()
        },
        hidden: excludeOptions.includes('addFriend')
        || playerValidator.isLocalDiscordPlayer
        || !playerValidator.isUser
        || isCurrentUser,
    }

    const viewProfileOption = {
        label: <T id={{key: 'strings:game.playerOptionsPopup.viewProfile'}} />,
        onClick: () => {
            UIViewProfilePopup.init(playerValidator.username)
        },
        hidden: excludeOptions.includes('viewProfile')
        || playerValidator.isLocalDiscordPlayer
        || !playerValidator.isUser,
    }

    const blockTradesOption = {
        label: <T id={{key: isTradeBlocked ? 'strings:game.playerOptionsPopup.unblockTrades' : 'strings:game.playerOptionsPopup.blockTrades'}} />,
        onClick: () => {
            onClickToggleEmbargoAgainstPlayer(playerValidator.color)
        },
        hidden: excludeOptions.includes('blockTrades')
        || isReplay
        || isPlayerMyPerspective
        || isCurrentUser
        || isMyColorSpectator,
    }

    const muteOption = {
        label: <T
            id={{key: isPlayerMuted ? 'strings:game.playerOptionsPopup.unmute' : 'strings:game.playerOptionsPopup.mute'}}
        />,
        onClick: () => {
            toggleMuteForPlayer(playerValidator.username)
        },
        hidden: excludeOptions.includes('mute')
        || isReplay
        || playerValidator.isLocalDiscordPlayer
        || isPlayerMyPerspective
        || UIUtilsEjsVariables.isUserOnDiscordMobile
        || !playerValidator.isUser
        || isCurrentUser
        || isMyColorSpectator,
    }

    const reportOption = {
        label: <div className={styles.report}><T id={{key: 'strings:game.playerOptionsPopup.report'}} /></div>,
        onClick: () => {
            // TODO: Migrate All Dialog to React
            UIPopupReportPlayer.createReportPopup(playerValidator.username, (playerName: string, reportReason: UserReportReasonEnum) => {
                const databaseGameId = socket.gameListener.databaseGameId
                const userReportData: AjaxDataTypes.UserReportData = {reason: reportReason, databaseGameId: databaseGameId}
                void UIAjaxWrapper.postRequestHandler(UIAPIEndpoints.apiReportPlayer(playerName), undefined, userReportData, 'application/json')
                sendReportPlayer(reportReason, playerValidator.color)
            })
        },
        hidden: excludeOptions.includes('report')
        || playerValidator.isLocalDiscordPlayer
        || !playerValidator.isUser
        || isCurrentUser,
    }

    const options = [
        sendGiftOption,
        addFriendOption,
        viewProfileOption,
        blockTradesOption,
        muteOption,
        reportOption,
    ]

    const isEmptyOption = useMemo(() => options.every((option) => option.hidden), [options])

    if(isEmptyOption) {
        <div className={className}>
            {children}
        </div>
    }
    return <Dropdown className={className} options={options}>
        {children}
    </Dropdown>
}
